/**
 * A retry that backs off and gives up (DV-3421). For a background check that
 * failed transiently and would otherwise wait for the next focus or storage
 * event — which a tab nobody touches may never get — without hammering a
 * service that is down: 2 s, 4 s, 8 s, 16 s, 32 s, then nothing until reset.
 */
export const BASE_RETRY_MS = 2_000;
export const MAX_RETRIES = 5;

export interface BoundedRetry {
  /** Run `fn` after the next delay. False when one is already pending, or retries are spent. */
  schedule(fn: () => void): boolean;
  /** Back to the first delay, cancelling anything pending. Call on success. */
  reset(): void;
}

export const createBoundedRetry = (): BoundedRetry => {
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    schedule(fn) {
      if (timer !== null || attempt >= MAX_RETRIES) return false;
      const delay = BASE_RETRY_MS * 2 ** attempt;
      attempt += 1;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, delay);
      return true;
    },
    reset() {
      attempt = 0;
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
};
